import {
  GSI_HISTORICAL_PHOTO_LAYERS,
  NENDO_PHOTO_LAYER_NAME,
  NENDO_PHOTO_MIN_ZOOM,
} from "@/features/map/constants/gsiPhotoLayers";
import type { TileCoverageStatus } from "./tileCoverage";

/** レイヤー名 → 表示範囲のデータ有無 */
export type PhotoCoverageMap = Record<string, TileCoverageStatus>;

export interface PhotoCoverageNoticeItem {
  label: string;
  status: TileCoverageStatus;
  minZoom: number;
}

/** 年度別空中写真で選択中の年度（`名前_2019` 形式のキーから取り出す） */
export const getSelectedNendoPhotoYear = (
  overlayVisibility: Record<string, boolean>
): number | null => {
  if (!overlayVisibility[NENDO_PHOTO_LAYER_NAME]) return null;
  const prefix = `${NENDO_PHOTO_LAYER_NAME}_`;
  for (const [key, visible] of Object.entries(overlayVisibility)) {
    if (!visible || !key.startsWith(prefix)) continue;
    const year = Number(key.slice(prefix.length));
    if (Number.isInteger(year)) return year;
  }
  return null;
};

/**
 * 地図上の通知に出す項目を、年度別空中写真 → 年代別（新しい順）の順で組み立てる。
 * 判定結果（coverage）が無いレイヤーは OFF か判定前なので含めない。
 */
export const buildPhotoCoverageNoticeItems = (
  coverage: PhotoCoverageMap,
  overlayVisibility: Record<string, boolean>
): PhotoCoverageNoticeItem[] => {
  const items: PhotoCoverageNoticeItem[] = [];

  const nendoStatus = coverage[NENDO_PHOTO_LAYER_NAME];
  const nendoYear = getSelectedNendoPhotoYear(overlayVisibility);
  if (nendoStatus && nendoYear !== null) {
    items.push({
      label: `${nendoYear}年の空中写真`,
      status: nendoStatus,
      minZoom: NENDO_PHOTO_MIN_ZOOM,
    });
  }

  for (const layer of GSI_HISTORICAL_PHOTO_LAYERS) {
    const status = coverage[layer.name];
    if (!status) continue;
    items.push({ label: layer.label, status, minZoom: layer.minZoom });
  }

  return items;
};

const BADGE_TEXT: Partial<Record<TileCoverageStatus, string>> = {
  none: "データなし",
  partial: "一部のみ",
};

/** レイヤーコントロールの項目横に出す短いバッジ（データなし・一部のみ） */
export const buildPhotoCoverageBadges = (
  coverage: PhotoCoverageMap
): Record<string, string> => {
  const badges: Record<string, string> = {};
  for (const [name, status] of Object.entries(coverage)) {
    const text = BADGE_TEXT[status];
    if (text) badges[name] = text;
  }
  return badges;
};
